import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ensurePushSubscribed,
  getPushEnvironment,
  isPushSupported,
  syncExistingPushSubscription,
} from "@/lib/pushNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/queries/account";
import { useUserMatchesSummary } from "@/queries/matches";
import { getIntroStorageKey, readIntroCompleted } from "@/utils/tutorialStorage";
import { toast } from "sonner";

const STORAGE_KEY = "lapstr:pwa-notification-onboarding";
const INSTALL_KEY = `${STORAGE_KEY}:installed-at`;
const DISMISSED_AT_KEY = `${STORAGE_KEY}:dismissed-at`;
const NUDGE_META_KEY = `${STORAGE_KEY}:nudge-meta`;
const SESSION_SHOWN_KEY = `${STORAGE_KEY}:session-shown`;
const PROMPT_COOLDOWN_MS = 5 * 24 * 60 * 60 * 1000;
const NUDGE_INTERVALS_MS = [
  0,
  14 * 24 * 60 * 60 * 1000,
  30 * 24 * 60 * 60 * 1000,
  60 * 24 * 60 * 60 * 1000,
  180 * 24 * 60 * 60 * 1000,
];

function getStoredState() {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch (_) {
    return undefined;
  }
}

function setStoredState(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (_) {
    // ignore storage errors
  }
}

function markInstalled() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(INSTALL_KEY, String(Date.now()));
  } catch (_) {
    // ignore storage errors
  }
}

function hasInstallMarker() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(INSTALL_KEY) != null;
  } catch (_) {
    return false;
  }
}

const INSTALL_STATES = new Set(["pending", "dismissed", "granted", "denied", "unsupported"]);

function markPending(force = false) {
  const current = getStoredState();
  if (!force && current && INSTALL_STATES.has(current) && current !== "pending") return false;
  setStoredState("pending");
  return true;
}

function markCompleted(status) {
  setStoredState(status);
}

function getDismissedAt() {
  if (typeof window === "undefined") return 0;
  try {
    return Number(window.localStorage.getItem(DISMISSED_AT_KEY) || 0);
  } catch (_) {
    return 0;
  }
}

function setDismissedAt(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DISMISSED_AT_KEY, String(value));
  } catch (_) {
    // ignore storage errors
  }
}

function clearDismissedAt() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DISMISSED_AT_KEY);
  } catch (_) {
    // ignore storage errors
  }
}

function getNudgeMeta() {
  if (typeof window === "undefined") return { shownCount: 0, nextEligibleAt: 0 };
  try {
    const raw = window.localStorage.getItem(NUDGE_META_KEY);
    if (!raw) return { shownCount: 0, nextEligibleAt: 0 };
    const parsed = JSON.parse(raw);
    return {
      shownCount: Number(parsed?.shownCount || 0),
      nextEligibleAt: Number(parsed?.nextEligibleAt || 0),
      lastShownAt: Number(parsed?.lastShownAt || 0),
    };
  } catch (_) {
    return { shownCount: 0, nextEligibleAt: 0 };
  }
}

function setNudgeMeta(meta) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NUDGE_META_KEY, JSON.stringify(meta));
  } catch (_) {
    // ignore storage errors
  }
}

function markNudgeShown() {
  const now = Date.now();
  const current = getNudgeMeta();
  const nextShownCount = Number(current.shownCount || 0) + 1;
  const intervalIndex = Math.min(nextShownCount, NUDGE_INTERVALS_MS.length - 1);
  const nextEligibleAt = now + NUDGE_INTERVALS_MS[intervalIndex];
  setNudgeMeta({
    shownCount: nextShownCount,
    lastShownAt: now,
    nextEligibleAt,
  });
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
    } catch (_) {
      // ignore storage errors
    }
  }
}

function hasShownThisSession() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SESSION_SHOWN_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (typeof navigator !== "undefined" && "standalone" in navigator && navigator.standalone)
  );
}

export default function PushPermissionPrompt() {
  const { user, ready } = useAuth() || {};
  const { data: notificationsData, isLoading: notificationsLoading } = useNotifications({
    enabled: ready && !!user,
  });
  const { data: userMatchesSummary, isLoading: userMatchesSummaryLoading } = useUserMatchesSummary({
    enabled: ready && !!user,
    recentLimit: 1,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [syncingGrantedPermission, setSyncingGrantedPermission] = useState(false);
  const [introCompleted, setIntroCompleted] = useState(false);
  const [permission, setPermission] = useState(() => {
    if (typeof Notification === "undefined") return "default";
    return Notification.permission;
  });

  const introStorageKey = useMemo(
    () => (user ? getIntroStorageKey(user) : null),
    [user]
  );

  const pushDemandedBySettings = useMemo(() => {
    const matchReminderEnabled = !!notificationsData?.push?.match_reminder?.enabled;
    const lineupUpdatesEnabled = !!notificationsData?.push?.lineup_updates?.enabled;
    const generalNewsEnabled = !!notificationsData?.push?.general_news?.enabled;
    return matchReminderEnabled || lineupUpdatesEnabled || generalNewsEnabled;
  }, [notificationsData]);

  const savedProtocolsCount = useMemo(
    () => Number(userMatchesSummary?.count || 0),
    [userMatchesSummary]
  );

  const pushNudgeEligible = useMemo(() => {
    if (savedProtocolsCount < 1) return false;
    if (hasShownThisSession()) return false;
    const meta = getNudgeMeta();
    return !meta.nextEligibleAt || Date.now() >= meta.nextEligibleAt;
  }, [savedProtocolsCount]);

  useEffect(() => {
    if (!ready || !user || !introStorageKey) {
      setIntroCompleted(false);
      return;
    }

    const syncIntroState = () => {
      setIntroCompleted(readIntroCompleted(introStorageKey));
    };

    syncIntroState();
    window.addEventListener("speedway:intro:completed", syncIntroState);
    return () => window.removeEventListener("speedway:intro:completed", syncIntroState);
  }, [introStorageKey, ready, user]);

  const shouldPrompt = useCallback(() => {
    if (typeof window === "undefined") return false;
    if (!ready || !user || !introCompleted) return false;

    if (!isPushSupported()) {
      markCompleted("unsupported");
      return false;
    }

    if (!isStandalone()) {
      return false;
    }

    if (Notification.permission === "granted") {
      markCompleted("granted");
      clearDismissedAt();
      return false;
    }

    const promptRelevant = pushDemandedBySettings || pushNudgeEligible;

    if (Notification.permission === "denied") {
      const dismissedAt = getDismissedAt();
      return promptRelevant && (!dismissedAt || Date.now() - dismissedAt >= PROMPT_COOLDOWN_MS);
    }

    const state = getStoredState();
    const dismissedAt = getDismissedAt();
    const cooldownActive = dismissedAt && Date.now() - dismissedAt < PROMPT_COOLDOWN_MS;
    if (promptRelevant) {
      return !cooldownActive && state !== "granted" && state !== "unsupported";
    }

    return !cooldownActive && state === "pending";
  }, [introCompleted, pushDemandedBySettings, pushNudgeEligible, ready, user]);

  const evaluate = useCallback(() => {
    if (!ready || !user) {
      setOpen(false);
      setChecking(false);
      return;
    }

    if (typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    }

    setOpen(shouldPrompt());
    setChecking(false);
  }, [ready, shouldPrompt, user]);

  useEffect(() => {
    if (!open) return;
    markNudgeShown();
  }, [open]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!ready || !user) {
      setOpen(false);
      setChecking(false);
      return undefined;
    }

    const handleAppInstalled = () => {
      markInstalled();
      markPending(true);
      window.setTimeout(evaluate, 500);
    };

    const mediaQuery = window.matchMedia?.("(display-mode: standalone)");
    const handleDisplayChange = (event) => {
      if (event.matches) {
        if (!hasInstallMarker()) {
          markInstalled();
          markPending(true);
        } else {
          markPending();
        }
        evaluate();
      }
    };

    window.addEventListener("appinstalled", handleAppInstalled);
    mediaQuery?.addEventListener?.("change", handleDisplayChange);

    const handleVisibility = () => {
      if (!document.hidden) evaluate();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    if (isStandalone()) {
      if (!hasInstallMarker()) {
        markInstalled();
        markPending(true);
      } else if (!getStoredState()) {
        markPending();
      }
    }

    const timer = window.setTimeout(evaluate, introCompleted ? 1200 : 0);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
      mediaQuery?.removeEventListener?.("change", handleDisplayChange);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearTimeout(timer);
    };
  }, [evaluate, introCompleted, ready, user]);

  useEffect(() => {
    if (!ready || !user || !introCompleted) return;
    if (!isStandalone()) return;
    if (notificationsLoading || userMatchesSummaryLoading) return;
    if (!pushDemandedBySettings) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (syncingGrantedPermission) return;

    let cancelled = false;
    setSyncingGrantedPermission(true);

    syncExistingPushSubscription()
      .then((synced) => {
        if (cancelled) return;
        if (synced) {
          markCompleted("granted");
          clearDismissedAt();
          setOpen(false);
          setPermission("granted");
        }
      })
      .catch(() => {
        // Tyst sync-försök. Om det misslyckas kan användaren fortfarande hantera det via prompt eller inställningar.
      })
      .finally(() => {
        if (!cancelled) setSyncingGrantedPermission(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    introCompleted,
    notificationsLoading,
    userMatchesSummaryLoading,
    pushDemandedBySettings,
    ready,
    syncingGrantedPermission,
    user,
  ]);

  const env = useMemo(() => getPushEnvironment(), []);

  const handleEnable = async () => {
    setRequesting(true);
    try {
      await toast.promise(ensurePushSubscribed(), {
        loading: "Aktiverar notiser...",
        success: "Notiser aktiverade",
        error: (e) => e?.message || "Kunde inte aktivera notiser",
      });
      markCompleted("granted");
      clearDismissedAt();
      setOpen(false);
      setPermission("granted");
    } catch (error) {
      if (typeof Notification !== "undefined" && Notification.permission === "denied") {
        markCompleted("denied");
        setPermission("denied");
      }
    } finally {
      setRequesting(false);
    }
  };

  const handleDismiss = () => {
    markCompleted("dismissed");
    setDismissedAt(Date.now());
    setOpen(false);
  };

  const handleOpenSettings = () => {
    markCompleted("denied");
    setOpen(false);
    toast("Tillåt notiser för Lapstr i webbläsarens eller enhetens inställningar.");
  };

  if (checking || notificationsLoading || userMatchesSummaryLoading) return null;
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDismiss();
      }}
    >
      <DialogContent
        className="max-w-sm"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Vill du aktivera notiser?</DialogTitle>
          <DialogDescription>
            {permission === "denied"
              ? "Du behöver tillåta notiser i webbläsarens eller enhetens inställningar för att få matchpåminnelser och viktiga uppdateringar i appen."
              : pushDemandedBySettings
                ? "Du har redan notiser aktiverade i dina inställningar. Tillåt nu push-notiser så att den installerade appen faktiskt kan leverera dem."
                : "Tillåt push-notiser så får du laguppställningar, matchpåminnelser och viktiga uppdateringar när du använder dina matchprotokoll."}
          </DialogDescription>
        </DialogHeader>
        {!env.https && (
          <p className="text-sm text-muted-foreground">
            Push kräver HTTPS eller localhost.
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" type="button" onClick={handleDismiss} disabled={requesting}>
            Inte nu
          </Button>
          {permission === "denied" ? (
            <Button type="button" onClick={handleOpenSettings}>
              Så aktiverar du notiser
            </Button>
          ) : (
            <Button type="button" onClick={handleEnable} disabled={requesting}>
              Tillåt notiser
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
